import { useField } from 'formik';
import { ChangeEvent, ClipboardEvent, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { isString, trim } from 'lodash';
import { TextAreaInputProps } from './types';
import SummaryDisplay from '../../SummaryDisplay';

const TextAreaInput = ({
    label,
    inlineHelp,
    disabled,
    name,
    id,
    rows,
    placeholder,
    maxCharacters,
    containerClassName = '', // default props
    className = '', // default props
    spellCheck = false, // default props
    isSummary,
}: TextAreaInputProps) => {
    const [_field, _meta, _fieldHelper] = useField(name);
    const helpId = inlineHelp ? `help-${id || name}` : undefined;
    const [pasted, setPasted] = useState(false);

    if (isSummary) {
        return (
            <SummaryDisplay
                label={label}
                as='p'
                className='pre-wrap'
                value={_field.value}
                id={id || name}
            />
        );
    }

    const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
        setPasted(true);
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        let text = event.target.value;

        if (pasted) {
            // Using the regex in yupAllowedFormat(false)
            text = text.replace(/[^0-9A-Za-z '.\u2018&#\\\u2013\u2019\u2014$%!?<>+:;/()+,@=^~*`"\u201C\u201D{}[\]|_-]/gi, '');
            setPasted(false);
        }
        _fieldHelper.setValue(text);
    };

    const handleOnBlur = () => {
        let newValue = _meta.value;
        if (isString(_meta.value)) {
            newValue = trim(_meta.value);
        }
        _fieldHelper.setTouched(true);
        _fieldHelper.setValue(newValue);
    };

    let hasExceededMaximum = false;
    let counterText = '';

    if (maxCharacters && maxCharacters > 0) {
        const currCharacters = _field.value ? _field.value.trim().length : 0;
        counterText = `${currCharacters} of ${maxCharacters} characters used`;
        hasExceededMaximum = (currCharacters > Number(maxCharacters));
    }

    return (
        <Form.Group className={`form-field-container ${containerClassName}`} controlId={id || name}>
            <Form.Label>{label}</Form.Label>
            {inlineHelp
        && (
            <Form.Text as='p' id={helpId} className='contextual-help'>
                {inlineHelp}
            </Form.Text>
        ) }
            <Form.Control
                className={`form-field form-text-area ${className}`}
                as='textarea'
                rows={rows || 3}
                {..._field}
                isInvalid={!!(_meta.touched && _meta.error)}
                disabled={disabled}
                aria-describedby={
                    _meta.touched && _meta.error
                        ? `${id || name}-validation-msg`
                        : (`${helpId}` || undefined)
                }
                onChange={handleChange}
                onPaste={handlePaste}
                onBlur={handleOnBlur}
                placeholder={placeholder}
                spellCheck={spellCheck}
            />
            {maxCharacters
      && (
          <div className={`text-end ${hasExceededMaximum ? 'counterLabelExceedsMax' : 'counterLabelWithinMax'}`}>
              <span className='small'>{counterText}</span>
          </div>
      )}
            {_meta.touched && _meta.error
                ? (
                    <Form.Control.Feedback
                        type='invalid'
                        id={`${id || name}-validation-msg`}
                        className='form-validation-message'
                    >
                        {_meta.error}
                        {hasExceededMaximum && (
                            <span className='visually-hidden'>
                                {', '}
                                {counterText}
                            </span>
                        )}
                    </Form.Control.Feedback>
                )
                : null}
        </Form.Group>
    );
};

// TextAreaInput.defaultProps = {
//     containerClassName: '',
//     className: '',
// };

export default TextAreaInput;
